import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { TableWithSearch } from "@/components";
import type { DataTableFeatures } from "@/components/data-table/features";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { useCurrentStore, usePaginatedSearch, useProviders } from "../../hooks";

const Providers = () => {
	const { t } = useTranslation();
	useBreadCrumbContext([{ label: t("providers.pageTitle") }]);
	const currentStore = useCurrentStore();
	const { searchQuery, page, setPage, onSearchQueryChange } =
		usePaginatedSearch();
	const { data, isLoading } = useProviders({
		page,
		"q[nameOrNotesCont]": searchQuery,
		"q[storeIdEq]": currentStore?.id,
		"q[s]": "name",
	});

	const columnHelper = createColumnHelper<DataTableFeatures, Provider>();

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor("name", {
					header: t("providers.table.name"),
					cell: (props) => (
						<Link
							className="underline"
							to={`/providers/${props.row.original.id}/edit`}
						>
							{props.getValue()}
						</Link>
					),
				}),
			] as ColumnDef<DataTableFeatures, Provider>[],
		[t, columnHelper],
	);

	return (
		<TableWithSearch
			searchPlaceholder={t("providers.searchPlaceholder")}
			onSearchQueryChange={onSearchQueryChange}
			searchQuery={searchQuery}
			isLoading={isLoading}
			createUrl="/providers/new"
			createLabel={t("providers.createButtonLabel")}
			columns={columns}
			data={data?.data}
			currentPage={page}
			totalPages={data?.headers["total-pages"]}
			onPageChange={setPage}
		/>
	);
};

export default Providers;
